import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, X } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    { value: 'all', label: 'All Products' },
    { value: 'socks', label: 'Socks' },
    { value: 'apparel', label: 'Apparel' }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
    setShowFilters(false);
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#09090b] text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 
              className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight"
              data-testid="shop-title"
            >
              Shop
            </h1>
            <p className="text-zinc-400 mt-4 max-w-xl">
              Performance gear built for young athletes who want to push harder, jump higher, and level up.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Filters & Products */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        {/* Filter Bar */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-zinc-200">
          <div className="hidden md:flex gap-4">
            {categories.map(cat => (
              <Button
                key={cat.value}
                variant={selectedCategory === cat.value ? 'default' : 'outline'}
                onClick={() => handleCategoryChange(cat.value)}
                className={`uppercase tracking-wider text-xs font-bold rounded-full ${
                  selectedCategory === cat.value 
                    ? 'bg-[#09090b] text-white hover:bg-zinc-800' 
                    : 'border-zinc-300 hover:border-[#09090b]'
                }`}
                data-testid={`filter-${cat.value}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>

          {/* Mobile Filter Toggle */}
          <Button
            variant="outline"
            className="md:hidden flex items-center gap-2 uppercase tracking-wider text-xs font-bold"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-toggle"
          >
            <Filter className="w-4 h-4" />
            Filter
          </Button>

          <p className="text-zinc-500 text-sm" data-testid="product-count">
            {filteredProducts.length} products
          </p>
        </div>

        {/* Mobile Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mb-6 pb-6 border-b border-zinc-200"
          >
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <Button
                  key={cat.value}
                  variant={selectedCategory === cat.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleCategoryChange(cat.value)}
                  className={`uppercase tracking-wider text-xs font-bold rounded-full ${
                    selectedCategory === cat.value 
                      ? 'bg-[#09090b] text-white' 
                      : 'border-zinc-300'
                  }`}
                >
                  {cat.label}
                </Button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="animate-pulse">
                <div className="bg-zinc-100 aspect-square" />
                <div className="mt-4 h-4 bg-zinc-100 rounded w-3/4" />
                <div className="mt-2 h-4 bg-zinc-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-zinc-500 text-lg">No products found in this category.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => handleCategoryChange('all')}
            >
              View All Products
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Link 
                  to={`/product/${product.id}`}
                  className="group block"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="relative aspect-square bg-zinc-100 overflow-hidden card-hover">
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${product.stockQty === 0 ? 'opacity-50' : ''}`}
                    />
                    {product.stockQty === 0 ? (
                      <span className="absolute top-4 left-4 bg-[#ff4d6d] text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
                        Sold Out
                      </span>
                    ) : product.category && (
                      <span className="absolute top-4 left-4 bg-black text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
                        {product.category}
                      </span>
                    )}
                  </div>
                  <div className="mt-4">
                    <h3 className="font-heading font-bold text-base uppercase tracking-tight group-hover:text-[#00ff9d] transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-zinc-500 text-sm mt-1 line-clamp-2">
                      {product.description}
                    </p>
                    <p className={`font-bold text-lg mt-2 ${product.stockQty === 0 ? 'text-zinc-400' : ''}`}>
                      ${product.price.toFixed(2)} <span className="text-zinc-400 text-sm font-normal">AUD</span>
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
